/* eslint-disable no-unused-vars */
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  colors,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ImageIcon from '@material-ui/icons/Image';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import { theme } from 'src/theme';
import {
  addTicketComment,
  deleteTicketComment,
  fetchTicketViaCustomerIdComment,
  taptapUploadMultipleImages,
  updateTicketComment
} from 'src/redux/slices/taptap-customer';
import useUserInfo from 'src/hooks/user/use-user-info';
import { usePermissions, useSnackBar } from 'src/hooks';
import { SupabaseClientService } from 'src/services';

const COMMENTS_PAGE_SIZE = 10;
const FOR_RMA_TAG = 'FOR RMA';

const useStyles = makeStyles({
  root: {
    backgroundColor: colors.common.white,
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
    maxHeight: 700,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  commentList: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5)
  },
  pinnedSection: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.25),
    border: `1px solid ${colors.amber[200]}`,
    borderRadius: 8,
    backgroundColor: '#fffdf5'
  },
  pinnedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1)
  },
  pinnedHeaderLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  pinnedCard: {
    border: `1px solid ${colors.amber[100]}`,
    borderLeft: `4px solid ${colors.amber[700]}`,
    backgroundColor: colors.common.white,
    borderRadius: 6,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1)
  },
  pinnedCardLast: {
    marginBottom: 0
  },
  pinnedMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75)
  },
  systemBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: colors.amber[900],
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0
  },
  systemTimePill: {
    color: colors.amber[900],
    backgroundColor: colors.amber[50],
    border: `1px solid ${colors.amber[100]}`,
    borderRadius: 999,
    padding: theme.spacing(0.25, 0.75),
    whiteSpace: 'nowrap'
  },
  pinnedTitle: {
    color: colors.grey[900],
    fontWeight: 700,
    whiteSpace: 'pre-line'
  },
  pinnedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  pinnedListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    color: colors.grey[900],
    backgroundColor: colors.common.white,
    border: `1px solid ${colors.amber[100]}`,
    borderRadius: 6,
    padding: theme.spacing(0.75, 1)
  },
  pinnedCount: {
    color: colors.amber[900],
    backgroundColor: colors.amber[50],
    border: `1px solid ${colors.amber[100]}`,
    borderRadius: 999,
    padding: theme.spacing(0.25, 0.75)
  },
  commentItem: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 13,
    backgroundColor: colors.blue[500],
    flexShrink: 0
  },
  bubble: {
    flex: 1
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: 2
  },
  attachedImg: {
    marginTop: theme.spacing(0.5),
    width: 120,
    height: 120,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${colors.grey[300]}`,
    cursor: 'pointer',
    display: 'block'
  },
  actions: {
    display: 'flex',
    gap: 2,
    marginTop: 2,
    opacity: 0,
    '$commentItem:hover &': {
      opacity: 1
    }
  },
  inputBox: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${colors.grey[200]}`
  },
  imagePreviewWrap: {
    position: 'relative',
    display: 'inline-block',
    marginTop: theme.spacing(1),
    marginLeft: 40
  },
  previewImg: {
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${colors.grey[300]}`
  },
  emptyState: {
    color: colors.grey[400],
    fontStyle: 'italic',
    padding: theme.spacing(2, 0)
  }
});

interface Props {
  customerId?: number;
  status?: string;
  isReadOnly?: boolean;
  hasSystemGeneratedTicket?: (value: boolean) => void;
  onFetchingChange?: (value: boolean) => void;
}

type Comment = {
  id: number;
  created_at: string;
  user_id?: number | null;
  customer_id: number;
  full_name: string;
  comment?: string;
  img_url?: string;
  tag?: string;
  updated_at?: string;
  deleted_at?: string;
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const formatDate = (d: string) =>
  new Date(d).toLocaleString('en-PH', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const getRealtimeCommentPayload = (value: any): Comment | undefined =>
  value?.id ? value : undefined;

const isSystemGeneratedComment = (comment: Comment) =>
  comment?.user_id === null || comment?.user_id === undefined;

const isRmaSystemComment = (comment: Comment) => comment.tag === FOR_RMA_TAG;

const Component: FC<Props> = ({
  customerId,
  status,
  isReadOnly = false,
  hasSystemGeneratedTicket,
  onFetchingChange
}) => {
  const classes = useStyles();
  const snackbar = useSnackBar();
  const { userDetails } = useUserInfo();
  const {
    canViewTaptapCashierDashboard,
    canViewTaptapSalesDashboard,
    canViewAllTaptapDashboard
  } = usePermissions();
  const fileRef = useRef<HTMLInputElement>(null);
  const editFileRef = useRef<HTMLInputElement>(null);
  const commentListRef = useRef<HTMLDivElement>(null);
  const snackbarRef = useRef(snackbar);

  const currentUserId = userDetails?.id;
  const currentFullName =
    [userDetails?.first_name, userDetails?.last_name]
      .filter(Boolean)
      .join(' ') ||
    userDetails?.username ||
    'User';

  const [comments, setComments] = useState<Comment[]>([]);
  const [input, setInput] = useState('');
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [imgFile, setImgFile] = useState<File | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editInput, setEditInput] = useState('');
  const [editImgPreview, setEditImgPreview] = useState<string | null>(null);
  const [editImgFile, setEditImgFile] = useState<File | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [hasOlderComments, setHasOlderComments] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const canAddImageComment =
    canViewTaptapSalesDashboard || canViewAllTaptapDashboard;

  const systemComments = useMemo(() => {
    return comments.filter((comment) => {
      if (!isSystemGeneratedComment(comment)) return false;
      return !comment.deleted_at || isRmaSystemComment(comment);
    });
  }, [comments]);

  const regularComments = useMemo(() => {
    return comments.filter((comment) => !isSystemGeneratedComment(comment));
  }, [comments]);

  useEffect(() => {
    snackbarRef.current = snackbar;
  }, [snackbar]);

  useEffect(() => {
    hasSystemGeneratedTicket?.(systemComments.length > 0);
  }, [hasSystemGeneratedTicket, systemComments.length]);

  useEffect(() => {
    onFetchingChange?.(isFetching);
  }, [isFetching, onFetchingChange]);

  const showError = useCallback((message: string) => {
    snackbarRef.current.show({
      message,
      severity: 'error',
      useSound: true
    });
  }, []);

  //pang select ng image
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  //pag mag eedit ng image
  const handleEditImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditImgFile(file);
    setEditImgPreview(URL.createObjectURL(file));
  };

  const fetchComments = useCallback(async () => {
    if (!customerId) {
      setComments([]);
      hasSystemGeneratedTicket?.(false);
      setHasOlderComments(false);
      return;
    }

    setIsFetching(true);
    const { data, error } = await fetchTicketViaCustomerIdComment(customerId, {
      limit: COMMENTS_PAGE_SIZE
    });
    setIsFetching(false);

    if (error) {
      showError('Failed to fetch ticket comments.');
      return;
    }

    setComments(data || []);

    setHasOlderComments((data || []).length === COMMENTS_PAGE_SIZE);
  }, [customerId, hasSystemGeneratedTicket, showError]);

  //pang offset call ng mga older comments - max 10 per load
  const fetchOlderComments = useCallback(async () => {
    if (!customerId || isFetching || isFetchingOlder || !hasOlderComments) {
      return;
    }

    const oldestComment = comments[comments.length - 1];

    if (!oldestComment?.created_at) {
      return;
    }

    setIsFetchingOlder(true);
    const { data, error } = await fetchTicketViaCustomerIdComment(customerId, {
      limit: COMMENTS_PAGE_SIZE,
      beforeCreatedAt: oldestComment.created_at
    });
    setIsFetchingOlder(false);

    if (error) {
      showError('Failed to fetch older ticket comments.');
      return;
    }

    setComments((prev) => {
      const existingCommentIds = new Set(prev.map((comment) => comment.id));
      const olderComments = (data || []).filter(
        (comment) => !existingCommentIds.has(comment.id)
      );

      return [...prev, ...olderComments];
    });
    setHasOlderComments((data || []).length === COMMENTS_PAGE_SIZE);
  }, [
    comments,
    customerId,
    hasOlderComments,
    isFetching,
    isFetchingOlder,
    showError
  ]);

  const handleCommentListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;

      if (isNearBottom) {
        fetchOlderComments();
      }
    },
    [fetchOlderComments]
  );

  //add ng new comment iaadd din creds syempre
  const handleAdd = async () => {
    if (!customerId || (!input.trim() && !imgFile)) return;

    setIsSaving(true);

    try {
      let uploadedImgUrl: string | undefined;

      if (imgFile) {
        //nireuse ko lang yung storage upload sa supabase
        const uploadResponse = await taptapUploadMultipleImages(
          'screenshots/',
          [
            {
              file: imgFile,
              img_url: imgPreview || undefined,
              created_by: undefined,
              created_at: new Date().toISOString()
            }
          ]
        );

        if (!uploadResponse?.[0]?.img_url) {
          throw new Error('Failed to upload ticket comment image.');
        }

        uploadedImgUrl = uploadResponse[0].img_url;
      }

      const { data, error } = await addTicketComment({
        customer_id: customerId,
        user_id: currentUserId,
        full_name: currentFullName,
        comment: input.trim() || undefined,
        img_url: uploadedImgUrl
      });

      if (error) {
        throw error;
      }

      if (data) {
        setComments((prev) => [data, ...prev]);
      } else {
        await fetchComments();
      }
    } catch (error) {
      showError('Failed to add ticket comment.');
    }

    setIsSaving(false);
    setInput('');
    setImgPreview(null);
    setImgFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  //delete comment - clear state din
  const handleDelete = async (id: number) => {
    if (!customerId) return;

    setIsSaving(true);
    const { data, error } = await deleteTicketComment({
      customer_id: customerId,
      comment_id: id
    });
    setIsSaving(false);

    if (error) {
      showError('Failed to delete ticket comment.');
      return;
    }

    setComments((prev) => prev.map((c) => (c.id === id ? data || c : c)));
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditInput(c.comment || '');
    setEditImgPreview(c.img_url || null);
    setEditImgFile(null);
  };

  const handleSaveEdit = async (id: number) => {
    if (!customerId) return;

    setIsSaving(true);

    try {
      let imgUrl = editImgPreview;

      if (editImgFile) {
        const uploadResponse = await taptapUploadMultipleImages(
          'screenshots/',
          [
            {
              file: editImgFile,
              img_url: editImgPreview || undefined,
              created_by: undefined,
              created_at: new Date().toISOString()
            }
          ]
        );

        if (!uploadResponse?.[0]?.img_url) {
          throw new Error('Failed to upload ticket comment image.');
        }

        imgUrl = uploadResponse[0].img_url;
      }

      const { data, error } = await updateTicketComment({
        comment_id: id,
        customer_id: customerId,
        comment: editInput.trim() || undefined,
        img_url: imgUrl || null
      });

      if (error) {
        throw error;
      }

      setComments((prev) => prev.map((c) => (c.id === id ? data || c : c)));
    } catch (error) {
      showError('Failed to update ticket comment.');
    }

    setIsSaving(false);
    setEditingId(null);
    setEditInput('');
    setEditImgPreview(null);
    setEditImgFile(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, id: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(id);
    }
    if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  //component to render system generated comment - pag walang user_id matik
  const renderSystemGeneratedComment = (comment: Comment, index: number) => {
    const commentLines = (comment.comment || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const isRmaComment = isRmaSystemComment(comment);
    const title = isRmaComment
      ? FOR_RMA_TAG
      : commentLines[0] || 'System generated note';
    const itemLines = isRmaComment ? commentLines : commentLines.slice(1);
    const isResolvedRma = isRmaSystemComment(comment) && !!comment.deleted_at;

    return (
      <Box
        key={comment.id}
        className={`${classes.pinnedCard} ${
          index === systemComments.length - 1 ? classes.pinnedCardLast : ''
        }`}
      >
        <Box className={classes.pinnedMeta}>
          <Typography variant="caption" className={classes.systemBadge}>
            <AssignmentTurnedInIcon style={{ fontSize: 15 }} />
            System Update
          </Typography>
          <Box display="flex" alignItems="center" gridGap={6}>
            <Typography variant="caption" className={classes.systemTimePill}>
              {formatDate(comment.created_at)}
            </Typography>
          </Box>
        </Box>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 10,
            marginBottom: 5
          }}
        >
          <Typography variant="body2" className={classes.pinnedTitle}>
            {title}
          </Typography>

          {isResolvedRma ? (
            <Chip
              size="small"
              label="Resolved"
              style={{
                height: 22,
                backgroundColor: colors.green[600],
                color: colors.common.white,
                fontWeight: 700
              }}
            />
          ) : null}
        </Box>
        {itemLines.length > 0 && (
          <Box className={classes.pinnedList}>
            {itemLines.map((line, index) => (
              <Box
                key={`${comment.id}-${index}`}
                className={classes.pinnedListItem}
              >
                <CheckIcon
                  style={{
                    fontSize: 16,
                    color: colors.green[700],
                    marginTop: 1,
                    flexShrink: 0
                  }}
                />
                <Typography variant="body2">
                  {line.replace(/^-\s*/, '')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (!customerId) {
      return;
    }

    //pang detect if may new comments solid
    const ticketCommentsTableChanges = SupabaseClientService.channel(
      `ticket_comments_channel_${customerId}`
    )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ticket_comments',
          filter: `customer_id=eq.${customerId}`
        },
        (payload) => {
          const newComment = getRealtimeCommentPayload(payload['new']);
          const oldComment = getRealtimeCommentPayload(payload.old);

          if (payload.eventType === 'INSERT' && newComment?.id) {
            setComments((prev) => {
              if (prev.some((comment) => comment.id === newComment.id)) {
                return prev;
              }

              return [newComment, ...prev];
            });
          }

          if (payload.eventType === 'UPDATE' && newComment?.id) {
            setComments((prev) =>
              prev.map((comment) =>
                comment.id === newComment.id ? newComment : comment
              )
            );
          }

          if (payload.eventType === 'DELETE' && oldComment?.id) {
            setComments((prev) =>
              prev.filter((comment) => comment.id !== oldComment.id)
            );
          }
        }
      )
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Ticket comment realtime channel failed:', {
            status,
            error,
            customerId
          });
        }
      });

    return () => {
      SupabaseClientService.removeChannel(ticketCommentsTableChanges);
    };
  }, [customerId, fetchComments]);

  return (
    <Box className={classes.root}>
      <Typography
        variant="subtitle2"
        style={{ fontWeight: 600, marginBottom: 16 }}
      >
        Comments
      </Typography>
      {systemComments.length > 0 && (
        <Box className={classes.pinnedSection}>
          <Box className={classes.pinnedHeader}>
            <Box className={classes.pinnedHeaderLabel}>
              <AssignmentTurnedInIcon
                style={{ fontSize: 18, color: colors.amber[800] }}
              />
              <Typography
                variant="caption"
                style={{ color: colors.grey[800], fontWeight: 700 }}
              >
                Pinned System Updates
              </Typography>
            </Box>
            <Typography variant="caption" className={classes.pinnedCount}>
              {systemComments.length}
            </Typography>
          </Box>
          {systemComments.map(renderSystemGeneratedComment)}
        </Box>
      )}
      <div
        ref={commentListRef}
        className={classes.commentList}
        onScroll={handleCommentListScroll}
      >
        {isFetching ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : comments.length === 0 ? (
          <Typography variant="body2" className={classes.emptyState}>
            No comments yet.
          </Typography>
        ) : regularComments.length === 0 ? (
          <Typography variant="body2" className={classes.emptyState}>
            No user comments yet.
          </Typography>
        ) : (
          regularComments.map((c) => {
            const isDeleted = !!c.deleted_at;
            const isOwner = c.user_id === currentUserId;
            const isEditing = editingId === c.id;

            return (
              <Box key={c.id} className={classes.commentItem}>
                <Avatar className={classes.avatar}>
                  {getInitials(c.full_name)}
                </Avatar>
                <Box className={classes.bubble}>
                  <Box className={classes.nameRow}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: 600, color: colors.grey[800] }}
                    >
                      {c.full_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{ color: colors.grey[400] }}
                    >
                      {formatDate(c.created_at)}
                      {c.updated_at && ' · edited'}
                    </Typography>
                  </Box>

                  {isDeleted ? (
                    <Typography
                      variant="body2"
                      style={{ color: colors.grey[400], fontStyle: 'italic' }}
                    >
                      This comment was deleted.
                    </Typography>
                  ) : isEditing ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        size="small"
                        variant="outlined"
                        value={editInput}
                        onChange={(e) => setEditInput(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, c.id)}
                        autoFocus
                      />
                      {editImgPreview && (
                        <Box
                          className={classes.imagePreviewWrap}
                          style={{ marginLeft: 0, marginTop: 8 }}
                        >
                          <img
                            src={editImgPreview}
                            alt="preview"
                            className={classes.previewImg}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              backgroundColor: colors.grey[700],
                              padding: 2
                            }}
                            onClick={() => {
                              setEditImgPreview(null);
                              setEditImgFile(null);
                              if (editFileRef.current)
                                editFileRef.current.value = '';
                            }}
                          >
                            <CloseIcon
                              style={{ fontSize: 11, color: 'white' }}
                            />
                          </IconButton>
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" mt={0.5}>
                        <input
                          ref={editFileRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleEditImageSelect}
                        />
                        {canAddImageComment ? (
                          <Tooltip title="Change image">
                            <IconButton
                              size="small"
                              disabled={isSaving}
                              onClick={() => editFileRef.current?.click()}
                            >
                              <ImageIcon
                                style={{
                                  fontSize: 16,
                                  color: colors.grey[500]
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : null}

                        <Tooltip title="Save (Enter)">
                          <IconButton
                            size="small"
                            disabled={isSaving}
                            onClick={() => handleSaveEdit(c.id)}
                          >
                            <CheckIcon
                              style={{
                                fontSize: 16,
                                color: colors.green[600]
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel (Esc)">
                          <IconButton
                            size="small"
                            disabled={isSaving}
                            onClick={() => setEditingId(null)}
                          >
                            <CloseIcon
                              style={{
                                fontSize: 16,
                                color: colors.grey[500]
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {c.comment && (
                        <Typography
                          variant="body2"
                          style={{ color: colors.grey[800] }}
                        >
                          {c.comment}
                        </Typography>
                      )}
                      {c.img_url && (
                        <img
                          src={c.img_url}
                          alt="attachment"
                          className={classes.attachedImg}
                          onClick={() => window.open(c.img_url, '_blank')}
                        />
                      )}
                      {!isReadOnly && isOwner && (
                        <Box className={classes.actions}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => startEdit(c)}
                            >
                              <EditIcon
                                style={{
                                  fontSize: 14,
                                  color: colors.grey[400]
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              disabled={isSaving}
                              onClick={() => handleDelete(c.id)}
                            >
                              <DeleteIcon
                                style={{
                                  fontSize: 14,
                                  color: colors.grey[400]
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
        {isFetchingOlder && (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={18} />
          </Box>
        )}
      </div>

      {!isReadOnly && (
        <>
          {imgPreview && (
            <Box className={classes.imagePreviewWrap}>
              <img
                src={imgPreview}
                alt="preview"
                className={classes.previewImg}
              />
              <IconButton
                size="small"
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: colors.grey[700],
                  padding: 2
                }}
                onClick={() => {
                  setImgPreview(null);
                  setImgFile(null);
                  if (fileRef.current) fileRef.current.value = '';
                }}
              >
                <CloseIcon style={{ fontSize: 11, color: 'white' }} />
              </IconButton>
            </Box>
          )}

          <>
            <Box className={classes.inputBox}>
              <Avatar className={classes.avatar}>
                {getInitials(currentFullName)}
              </Avatar>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                variant="outlined"
                placeholder="Write a comment..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />
              {canAddImageComment ? (
                <Tooltip title="Attach image">
                  <IconButton
                    size="small"
                    onClick={() => fileRef.current?.click()}
                  >
                    <ImageIcon
                      style={{ fontSize: 20, color: colors.grey[500] }}
                    />
                  </IconButton>
                </Tooltip>
              ) : null}

              <Tooltip title="Send">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={isSaving || (!input.trim() && !imgFile)}
                    onClick={handleAdd}
                  >
                    <SendIcon style={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              style={{ color: colors.grey[400], marginLeft: 40 }}
            >
              Enter to send · Shift+Enter for new line
            </Typography>
          </>
        </>
      )}
    </Box>
  );
};

export const TicketComment = memo(Component);
